import {
  parseSparklinkFromDestinationUrl,
  parseSparklinkParams,
  SPARKLINK_CHAT_PARAM_KEYS,
  SPARKLINK_PARAM_CHAT_DESTINATION,
  type ParsedSparklink,
} from '@arthelper/shared/sparklink';
import {
  hasSparklinkUploadModalParam,
  SPARKLINK_UPLOAD_MODAL_PARAM_KEYS,
} from '@arthelper/shared/sparklinkUploadModal';
import type { ChatFilePart } from '~/components/Chat/utils/fileParts';

const AUTO_SUBMIT_SESSION_KEY = 'arty_sparklink_auto_submitted';

/**
 * A full URL on any host, a path with a query, a bare query string, or URLSearchParams: only
 * the query is read. URLs go through the shared parser so the accepted shape is defined once.
 */
export function toSparklinkParams(input: string | URLSearchParams): URLSearchParams {
  if (input instanceof URLSearchParams) {
    return new URLSearchParams(input);
  }

  const trimmed = input.trim();
  const isUrl = /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/');
  if (isUrl) {
    return parseSparklinkFromDestinationUrl(trimmed)?.searchParams ?? new URLSearchParams();
  }

  return new URLSearchParams(trimmed.replace(/^\?/, '').split('#')[0] ?? '');
}

export type UrlLanding = {
  /** `open` the flyout, `forward` the whole link to /chat, or only `strip` it from the URL. */
  action: 'open' | 'strip' | 'forward';
  params: URLSearchParams;
  /** The query to replace the URL's with, the sparklink's params gone. */
  search: string;
  /** The sparklink's own params, so a page rewriting its other params is still one landing. */
  key: string;
};

const SPARKLINK_KEYS: readonly string[] = [...SPARKLINK_CHAT_PARAM_KEYS, ...SPARKLINK_UPLOAD_MODAL_PARAM_KEYS];

function asSearch(params: URLSearchParams): string {
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * What a sparklink in the page's own URL does, as town-square's /community lands one: null when
 * the URL carries none. A link for the uploader, or an assistant link with a message or spark,
 * goes whole to /chat, which owns both and reads only those. A link with nothing to say or run
 * is only stripped.
 *
 * `hostsUploadModal` is a page saying it renders the forced uploader itself (`SparklinkUploadModal`),
 * so an uploader link stays put and lands here like any other instead of being handed to /chat.
 */
export function urlLanding(search: string, options?: { hostsUploadModal?: boolean }): UrlLanding | null {
  const params = new URLSearchParams(search);
  const parsed = parseSparklinkParams(params);
  if (!parsed.hasRecognizedParam) {
    return null;
  }

  const { own, rest } = [...params].reduce(
    (memo, [key, value]) => {
      (SPARKLINK_KEYS.includes(key) ? memo.own : memo.rest).append(key, value);
      return memo;
    },
    { own: new URLSearchParams(), rest: new URLSearchParams() }
  );
  const landing = { params, search: asSearch(rest), key: own.toString() };

  const forAssistant = parsed.destination === 'assistant' && !!(parsed.message || parsed.sparkId);
  const forUploader = hasSparklinkUploadModalParam(params) && !options?.hostsUploadModal;
  if (forUploader || forAssistant) {
    return { ...landing, action: 'forward' };
  }

  // Opened here, so the destination goes: `open` would otherwise send an assistant link to /chat.
  const opened = new URLSearchParams(params);
  opened.delete(SPARKLINK_PARAM_CHAT_DESTINATION);
  const hasContent = !!(parsed.shouldAutoSubmit || parsed.sparkId || parsed.context);
  return { ...landing, params: opened, action: hasContent ? 'open' : 'strip' };
}

/**
 * An uploader link that also has a spark, message, or context still lands as `open`. On a page
 * that draws the uploader itself, that open waits until the dialog is gone: opening the assistant
 * sheet in the same turn covers the dialog and hides its heading from assistive tech.
 */
export function uploaderHoldsFlyout(landing: UrlLanding, hostsUploadModal: boolean): boolean {
  return hostsUploadModal && landing.action === 'open' && hasSparklinkUploadModalParam(landing.params);
}

let deferredUploaderFlyout: URLSearchParams | null = null;
const deferredUploaderFlyoutListeners = new Set<() => void>();

function publishDeferredUploaderFlyout(): void {
  for (const listener of deferredUploaderFlyoutListeners) listener();
}

/** Remember the flyout open the uploader is holding. A later landing replaces the earlier one. */
export function deferFlyoutForUploader(params: URLSearchParams): void {
  deferredUploaderFlyout = new URLSearchParams(params);
  publishDeferredUploaderFlyout();
}

/** The held open, if one is waiting. Same reference until the next save or take. */
export function getDeferredUploaderFlyout(): URLSearchParams | null {
  return deferredUploaderFlyout;
}

/** Take the held open, once. Empty when nothing is waiting. */
export function takeDeferredUploaderFlyout(): URLSearchParams | null {
  const params = deferredUploaderFlyout;
  deferredUploaderFlyout = null;
  if (params) publishDeferredUploaderFlyout();
  return params;
}

/** Re-render when a hold is saved or taken. The page effect stores it after the dialog's effect. */
export function subscribeDeferredUploaderFlyout(listener: () => void): () => void {
  deferredUploaderFlyoutListeners.add(listener);
  return () => deferredUploaderFlyoutListeners.delete(listener);
}

// Same shape as town-square's key, so one landing is one send across both apps' conventions.
export function autoSubmitKey(parsed: ParsedSparklink, pathname: string): string {
  return [
    pathname,
    parsed.sparkId ?? '',
    parsed.context ?? '',
    parsed.message ?? '',
    parsed.images?.join(',') ?? '',
    parsed.imageIds?.join(',') ?? '',
  ].join('|');
}

const MEDIA_TYPES: Record<string, string> = {
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  avif: 'image/avif',
};

function pathOf(url: string): string {
  return url.split('#')[0]?.split('?')[0] ?? '';
}

function mediaType(url: string): string {
  const extension = pathOf(url).split('.').pop()?.toLowerCase() ?? '';
  return MEDIA_TYPES[extension] ?? 'image/jpeg';
}

/** A sparklink's `images` URLs, as the file parts its opening message carries. */
export function sparklinkFileParts(images?: string[]): ChatFilePart[] {
  return (images ?? []).map((url) => {
    const filename = pathOf(url).split('/').pop() || 'image';
    return { type: 'file', url, displayUrl: url, mediaType: mediaType(url), filename };
  });
}

export function wasAutoSubmitted(key: string): boolean {
  try {
    return sessionStorage.getItem(AUTO_SUBMIT_SESSION_KEY) === key;
  } catch {
    return false;
  }
}

export function markAutoSubmitted(key: string): void {
  try {
    sessionStorage.setItem(AUTO_SUBMIT_SESSION_KEY, key);
  } catch {
    // sessionStorage unavailable
  }
}

/** A deliberate re-open of the same link sends again; only an accidental reload should not. */
export function clearAutoSubmitted(key: string): void {
  try {
    if (sessionStorage.getItem(AUTO_SUBMIT_SESSION_KEY) === key) {
      sessionStorage.removeItem(AUTO_SUBMIT_SESSION_KEY);
    }
  } catch {
    // sessionStorage unavailable
  }
}
